/**
 * ModalManager.js
 * "The Traffic Controller"
 * Enforces a strict "One Modal Policy" to prevent infinite stacking and memory leaks.
 * [WCAG] Includes Focus Trap for accessibility compliance (WCAG 2.4.3)
 */

export class ModalManager {
    static currentModal = null;
    static currentOverlay = null;
    static _focusTrapHandler = null;
    static _previouslyFocused = null;

    /**
     * Show a modal, automatically closing any existing one.
     * @param {Object} ModalComponent - The Modal Class/Object (must have render() or show() method)
     * @param {*} params - Arguments to pass to the modal
     */
    static show(ModalComponent, ...params) {
        // 1. Cleanup existing modal
        this.closeAll();

        // [WCAG] Save currently focused element to restore later
        this._previouslyFocused = document.activeElement;

        // 2. Open new modal
        // Support both Class-based (new Modal().render()) and Singleton Object (Modal.show()) patterns
        try {
            if (typeof ModalComponent === 'function') {
                // Class-based
                const instance = new ModalComponent(...params);
                // If render returns something (like a promise?), return it. 
                // Though usually 'render' is void. But 'ConfirmModal' is an object pattern.
                if (instance.render) {
                    const result = instance.render();
                    this.currentModal = instance;
                    // [WCAG] Enable focus trap after render
                    requestAnimationFrame(() => this._enableFocusTrap());
                    return result;
                }
                this.currentModal = instance;
            } else if (typeof ModalComponent === 'object' && ModalComponent.show) {
                // Singleton Object
                const result = ModalComponent.show(...params);
                this.currentModal = ModalComponent;
                // [WCAG] Enable focus trap after render
                requestAnimationFrame(() => this._enableFocusTrap());
                return result;
            } else {
                console.error("ModalManager: Invalid Modal Component", ModalComponent);
            }
        } catch (e) {
            console.error("ModalManager: Failed to open modal", e);
        }
    }

    /**
     * Force close all known modals
     * (Relies on DOM cleanup mostly, as many modals manage their own DOM)
     */
    static closeAll() {
        // [WCAG] Disable focus trap first
        this._disableFocusTrap();

        // 1. Remove all generic overlays and wrappers by Z-Index or Class
        // This is a "Nuclear Option" to ensure clean slate.

        // Select all elements that look like modal wrappers or overlays
        // We target the z-indices defined in our new hierarchy (80, 90, 9990, 9999, etc.)
        const potentialModals = document.querySelectorAll(
            '[class*="z-[9990]"], [class*="z-[9999]"], [class*="z-[10000]"], [class*="z-[10001]"], .modal-overlay, .modal-wrapper, #auth-require-modal-overlay'
        );

        potentialModals.forEach(el => {
            // Check if it's the System Drawer (z-10000) - We might NOT want to close the drawer here?
            // User requirement: "Popup appears -> Drawer might stay?" 
            // Usually, opening a full modal might want to backdrop everything.
            // But let's be specific: Remove InfoModal, ReportModal, etc. 

            // If it has 'fixed inset-0', it's likely a modal overlay
            if (el.className.includes('fixed inset-0')) {
                // Check if it's NOT the Drawer Overlay (which might be z-45 or similar)
                // Drawer overlay is z-45 in old code, z-60 in new plan.
                // Modals are z-80+. 
                // Let's rely on the DOM removal.
                el.remove();
            }
        });

        this.currentModal = null;

        // 2. [FIX] Close Legacy Modals (IDs)
        // These modals use 'hidden' class toggling instead of DOM removal.
        const legacyIds = ['report-modal', 'proposal-modal', 'plaza-post-modal', 'info-modal'];
        legacyIds.forEach(id => {
            const el = document.getElementById(id);
            if (el) {
                // If it's a direct overlay, we might normally remove it, but legacy code expects it to persist?
                // Most legacy code toggles 'hidden'.
                el.classList.add('hidden');

                // If they created a separate overlay (like system-menu might), check that too.
                // But for now, hiding the main container is key.
            }
        });
    }

    // ============================
    // [WCAG] Focus Trap Utilities
    // ============================

    /**
     * Enable focus trap on the topmost modal overlay.
     * Tab/Shift+Tab cycles only within focusable elements inside the overlay.
     */
    static _enableFocusTrap() {
        // Find the topmost modal overlay
        const overlay = document.querySelector(
            '[class*="z-[9990]"], [class*="z-[9999]"], [class*="z-[10000]"], [class*="z-[10001]"]'
        );
        if (!overlay) return;

        // Set role and aria-modal for screen readers
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');

        const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

        this._focusTrapHandler = (e) => {
            if (e.key !== 'Tab') return;

            const focusable = overlay.querySelectorAll(FOCUSABLE);
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey) {
                // Shift+Tab: if at first element, jump to last
                if (document.activeElement === first || !overlay.contains(document.activeElement)) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                // Tab: if at last element, jump to first
                if (document.activeElement === last || !overlay.contains(document.activeElement)) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', this._focusTrapHandler);

        // Auto-focus the first focusable element
        const firstFocusable = overlay.querySelectorAll(FOCUSABLE);
        if (firstFocusable.length > 0) {
            firstFocusable[0].focus();
        }
    }

    /**
     * Disable focus trap and restore previous focus.
     */
    static _disableFocusTrap() {
        if (this._focusTrapHandler) {
            document.removeEventListener('keydown', this._focusTrapHandler);
            this._focusTrapHandler = null;
        }

        // Restore focus to the element that was active before modal opened
        if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
            try {
                this._previouslyFocused.focus();
            } catch (e) { /* element may have been removed */ }
            this._previouslyFocused = null;
        }
    }
}

